/* global debounce */

if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.maxQuantityRequestId = 0;
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.form.querySelector('[name="id"]').disabled = false;
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');
        this.form.addEventListener('submit', this.handleSubmit.bind(this));
        this.freezerBoxId = Number(window.theme.settings.freezerBoxId);
        this.freezerBoxAmount = Number(window.theme.settings.freezerBoxProductAmount);
        this.thermoBoxId = Number(window.theme.settings.thermoBoxId);
        this.thermoBoxAmount = Number(window.theme.settings.thermoBoxProductAmount);

        if (this.dataset.showQuantitySelector) {
          this.quantitySelector = this.querySelector('quantity-input');

          if (this.quantitySelector) {
            this.quantityInput = this.quantitySelector.querySelector('.qty-input__input');
            this.quantitySelector.addEventListener('change', debounce(this.handleChange.bind(this)));
            this.lineItemChangeListener = this.lineItemChangeListener
              || this.handleLineItemChange.bind(this);
            document.addEventListener('on:line-item:change', (evt) => {
              this.lineItemChangeListener(evt);
            });
          }
        }

        this.setMaximumInputQuantity();
        this.handleQuantityButtonState();
        document.addEventListener('on:variant:change', this.setMaximumInputQuantity.bind(this));
      }
    }

    disconnectedCallback() {
      document.removeEventListener('on:line-item:change', this.lineItemChangeListener);
    }

    /**
     * Sets the max attribute of the quantity input
     */
    async setMaximumInputQuantity() {
      const requestId = ++this.maxQuantityRequestId;
      const productInfo = this.closest('.product-info') || this.closest('quick-add-drawer');
      if (productInfo) {
        const quantitySelector = this.querySelector('quantity-input');
        if (quantitySelector) {
          const quantityInput = quantitySelector.querySelector('.qty-input__input');
          const dataEl = productInfo.querySelector('.js-inventory-data');
          if (dataEl) {
            const variantId = Number(this.form.querySelector('[name="id"]').value);
            const inventoryJson = JSON.parse(dataEl.textContent);
            const inventory = inventoryJson.find((item) => item.id === variantId);
            if (!inventory) {
              quantityInput.removeAttribute('max');
              const minusButton = quantitySelector.querySelector('.btn--minus');
              const plusButton = quantitySelector.querySelector('.btn--plus');
              ProductForm.setQuantityButtonState(null, quantityInput, minusButton, plusButton);
              return;
            }

            const maxByStock = ProductForm.computeMaxByStock(inventory);

            if (maxByStock !== null) {
              let maxAllowed = maxByStock;
              // "quantity" is add-to-cart amount; "updates[]" is cart absolute quantity editor.
              if (quantityInput.name === 'quantity') {
                const inCartQty = await this.getVariantQuantityInCart(variantId);
                if (requestId !== this.maxQuantityRequestId) return;
                maxAllowed = Math.max(0, maxByStock - inCartQty);
              }
              quantityInput.max = maxAllowed;
            } else {
              quantityInput.removeAttribute('max');
            }

            const currentQuantity = Number(quantityInput.value);
            const hasMax = quantityInput.max !== '';
            const maxQuantity = Number(quantityInput.max);
            if (hasMax && !Number.isNaN(maxQuantity) && maxQuantity >= 0 && currentQuantity > maxQuantity) {
              if (maxQuantity === 0) {
                quantityInput.value = '0';
              } else {
                const minQuantity = Number(quantityInput.min || 1);
                quantityInput.value = String(Math.max(minQuantity, maxQuantity));
              }
            }

            if (hasMax && maxQuantity === 0) {
              quantityInput.disabled = true;
              quantityInput.classList.add('is-out-of-stock');
              if (this.submitBtn && quantityInput.name === 'quantity') {
                this.submitBtn.dataset.stockLimitDisabled = 'true';
                this.submitBtn.disabled = true;
              }
            } else {
              quantityInput.disabled = false;
              quantityInput.classList.remove('is-out-of-stock');
              if (this.submitBtn && quantityInput.name === 'quantity' && this.submitBtn.dataset.stockLimitDisabled === 'true') {
                this.submitBtn.disabled = false;
                delete this.submitBtn.dataset.stockLimitDisabled;
              }
            }
          }

          const minusButton = quantitySelector.querySelector('.btn--minus');
          const plusButton = quantitySelector.querySelector('.btn--plus');
          ProductForm.setQuantityButtonState(null, quantityInput, minusButton, plusButton);
        }
      }
    }

    static computeMaxByStock(inventory) {
      if (!inventory) return null;
      if (inventory.location_configured) {
        const maxFromLocations = Number(inventory.inventory_quantity);
        return Number.isNaN(maxFromLocations) ? 0 : Math.max(0, maxFromLocations);
      }
      if (inventory.inventory_management.length !== 0 && inventory.inventory_policy !== 'continue') {
        const nativeMax = Number(inventory.inventory_quantity);
        return Number.isNaN(nativeMax) ? 0 : Math.max(0, nativeMax);
      }
      return null;
    }

    async getVariantQuantityInCart(variantId) {
      try {
        const response = await fetch(`${theme.routes.cart}.js`);
        if (!response.ok) return 0;
        const cart = await response.json();
        return ProductForm.getVariantQuantityInCartData(cart, variantId);
      } catch (e) {
        return 0;
      }
    }

    static getVariantQuantityInCartData(cartData, variantId) {
      return (cartData.items || []).reduce((sum, item) => {
        const itemVariantId = Number(item.variant_id || item.id);
        if (itemVariantId === variantId) return sum + Number(item.quantity || 0);
        return sum;
      }, 0);
    }

    async validateStock(variantId, productQuantity, oldCartData = null) {
      const productInfo = this.closest('.product-info') || this.closest('quick-add-drawer');
      const dataEl = productInfo ? productInfo.querySelector('.js-inventory-data') : null;
      const quantityInput = this.form.querySelector('[name="quantity"]');
      if (!dataEl || !quantityInput) {
        return { oldCartData };
      }

      const inventoryJson = JSON.parse(dataEl.textContent);
      const inventory = inventoryJson.find((item) => item.id === variantId);
      if (!inventory) {
        return { oldCartData };
      }

      const maxByStock = ProductForm.computeMaxByStock(inventory);
      if (maxByStock === null) {
        return { oldCartData };
      }

      let cartData = oldCartData;
      if (!cartData) {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        cartData = await oldCartResponse.json();
      }

      const inCartQty = ProductForm.getVariantQuantityInCartData(cartData, variantId);
      const remainingQty = Math.max(0, maxByStock - inCartQty);
      if (productQuantity > remainingQty) {
        const overLimitError = theme.strings.noStock || theme.strings.cartError || 'Not enough stock.';
        this.setErrorMsgState(overLimitError);
        throw new Error(overLimitError);
      }

      return { oldCartData: cartData, remainingQty, maxByStock };
    }

    /**
     * Sets the max attribute of the quantity input
     */
    handleQuantityButtonState() {
      const productInfo = this.closest('.product-info') || this.closest('quick-add-drawer');
      if (productInfo) {
        const quantitySelector = this.querySelector('quantity-input');
        if (quantitySelector) {
          const quantityInput = quantitySelector.querySelector('.qty-input__input');
          const minusButton = quantitySelector.querySelector('.btn--minus');
          const plusButton = quantitySelector.querySelector('.btn--plus');

          plusButton.addEventListener('click', () => {
            ProductForm.setQuantityButtonState('up', quantityInput, minusButton, plusButton);
          });

          minusButton.addEventListener('click', () => {
            ProductForm.setQuantityButtonState('down', quantityInput, minusButton, plusButton);
          });

          ProductForm.setQuantityButtonState(null, quantityInput, minusButton, plusButton);
        }
      }
    }

    static setQuantityButtonState(direction, quantityInput, minusButton, plusButton) {
      const currentQuantity = Number(quantityInput.value);
      const hasMax = quantityInput.max !== '';
      const max = Number(quantityInput.max);
      const min = Number(quantityInput.min);

      let newQuantity = currentQuantity;

      if (direction === 'up') {
        newQuantity = currentQuantity + 1;
      } else if (direction === 'down') {
        newQuantity = currentQuantity - 1;
      }

      // Enable or disable buttons based on the newQuantity relative to max and min
      minusButton.disabled = newQuantity <= min;
      plusButton.disabled = hasMax && (max === 0 || (max > 0 && newQuantity >= max));
    }

    /**
     * Handles 'change' events on the line item (dispatched by the theme).
     * @param {object} evt - Event object.
     */
    handleLineItemChange(evt) {
      if (evt.detail.variantId.toString() === this.dataset.variantId) {
        debounce(this.updateQuantityState(evt.detail.newQuantity));
      }
    }

    /**
     * Handles 'change' events on the quantity-input element.
     * @param {object} evt - Event object.
     */
    handleChange(evt) {
      this.updateQuantity(evt.target.value);
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      const variantId = Number(this.form.querySelector('[name="id"]').value);
      const productQuantity = Number(this.form.querySelector('[name="quantity"]')?.value ?? 1);
      const quantityInput = this.form.querySelector('[name="quantity"]');
      const maxQuantity = Number(quantityInput?.max);
      if (quantityInput && quantityInput.max !== '' && !Number.isNaN(maxQuantity) && productQuantity > maxQuantity) {
        this.setErrorMsgState(theme.strings.noStock || theme.strings.cartError);
        return;
      }

      let preflightCartData = null;
      try {
        const preflightValidation = await this.validateStock(variantId, productQuantity);
        preflightCartData = preflightValidation.oldCartData || null;
      } catch (error) {
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent || error.message
          }
        }));
        return;
      }

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const handle = this.form.querySelector('[name="handle"]').value;
      let sections = 'cart-icon-bubble,freezer-box-popup-content';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      const packageVolume = Number(this.form.getAttribute('package-volume'));
      const packageVolumeThermobox = Number(this.form.getAttribute('package-volume-thermobox'));

      // Get all quick order lists on the page
      const quickOrderSections = [];
      document.querySelectorAll('quick-order-list').forEach((quickOrderList) => {
        const closestSection = quickOrderList.closest('.shopify-section');
        if (closestSection) {
          const sectionId = closestSection.id.replace('shopify-section-', '');
          // sections.push({
          //   id: `quick-order-list_${sectionId}`,
          //   section: sectionId,
          //   selector: `#quick-order-list_${sectionId}`
          // });
          sections += `,${sectionId}`;
        }
      });

      // --- build properties from form inputs ---
      const properties = {};
      this.form.querySelectorAll('[name^="properties["]').forEach((input) => {
        const match = input.name.match(/^properties\[(.+)\]$/);
        if (match) {
          properties[match[1]] = input.value;
        }
      });

      // merge freezer/thermobox props
      if (this.freezerBoxId) {
        Object.assign(properties, {
          _withFreezerBox: this.freezerBoxId,
          _withThermoBox: this.thermoBoxId,
          _packageVolume: packageVolume,
          _packageVolumeThermobox: packageVolumeThermobox,
        });
      }

      const formData = {
        items: [
          {
            id: variantId,
            quantity: productQuantity,
            properties
          }
        ],
        sections,
        "sections_url": window.location.pathname,
      };

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();
        await this.validateStock(variantId, productQuantity, oldCartData || preflightCartData);

        const oldFreezerBox = oldCartData.items.find((item) => item.id === this.freezerBoxId);
        const oldFreezerBoxQuantity = oldFreezerBox?.quantity ?? 0;
        const oldThermoBox = oldCartData.items.find((item) => item.id === this.thermoBoxId);
        const oldThermoBoxQuantity = oldThermoBox?.quantity ?? 0;
        const sumPackageVolumes = window.sga.sumProductPackageVolumes(
          oldCartData.items,
          {
            quantity: productQuantity,
            packageVolume: packageVolume
          },
          this.freezerBoxAmount
        );

        const sumPackageVolumesThermobox = window.sga.sumProductPackageVolumes(
          oldCartData.items,
          {
            quantity: productQuantity,
            packageVolumeThermobox: packageVolumeThermobox
          },
          this.thermoBoxAmount,
          true
        );

        if (this.freezerBoxId && oldFreezerBoxQuantity < sumPackageVolumes) {
          formData.items.push({
            'id': this.freezerBoxId,
            'quantity': Math.ceil(sumPackageVolumes) - oldFreezerBoxQuantity,
            'properties': {
              _isFreezerBox: true,
              _freezerBoxAmount: this.freezerBoxAmount,
            }
          })
        }

        if (this.thermoBoxId && oldThermoBoxQuantity < sumPackageVolumesThermobox) {
          formData.items.push({
            'id': this.thermoBoxId,
            'quantity': Math.ceil(sumPackageVolumesThermobox) - oldThermoBoxQuantity,
            'properties': {
              _isThermoBox: true,
              _thermoBoxAmount: this.thermoBoxAmount,
            }
          });
        }

        const fetchRequestOpts = {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(formData)
        };

        const response = await fetch(`${theme.routes.cartAdd}.js`, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          if (oldFreezerBoxQuantity < sumPackageVolumes) {
            window.sga.showFreezerBoxModal(
              data,
              oldFreezerBoxQuantity,
              data.sections['freezer-box-popup-content']
            );
          }
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && ((theme.settings.afterAtc === 'drawer_desktop' && theme.mediaMatches.md)
                  || theme.settings.afterAtc === 'drawer'),
                variantId,
                handle
              );

              // Update the quick order lists on the page
              if (quickOrderSections.length) {
                quickOrderSections.forEach((quickOrderSection) => {
                  const sectionEl = document.getElementById(quickOrderSection.id);
                  if (!sectionEl) return;

                  const el = sectionEl.querySelector(quickOrderSection.selector) || sectionEl;
                  el.innerHTML = ProductForm.getElementHTML(
                    data.sections[quickOrderSection.section],
                    quickOrderSection.selector
                  );
                });
              }
            } else if (theme.settings.afterAtc === 'crossell_drawer' && window.preCartDrawer) {
              document.dispatchEvent(new CustomEvent('on:cart:add', {
                bubbles: true,
                detail: {
                  variantId: data.items[0].variant_id,
                  handle: handle
                }
              }));
              window.preCartDrawer.fetchCrossellDrawerHtml(variantId, handle);
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refreshCartItems();
                }
              }
            }
          }, 700);
        }

        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!newCartResponse.ok) throw new Error(newCartResponse.status);
        const newCartData = await newCartResponse.json();

        // Update cart attributes based on BARF products in cart
        await ProductForm.updateCartAttributesForFrozenProduct(newCartData);

        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];
        const itemInNewCart = newCartData.items.filter(
          (item) => item.variant_id === variantId
        )[0];

        let newQuantity = 1;

        // Check if product was already in the cart
        if (itemInOldCart) {
          newQuantity = (itemInOldCart.quantity === data.quantity)
            ? itemInOldCart.quantity : data.quantity;
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.items[0].variant_id,
              handle: handle,
            }
          }));

          // Update the quantity selectors if needed
          if (this.quantitySelector) {
            this.setAttribute('data-show-quantity-selector', 'true');
            this.quantityInput.value = String(newQuantity);
            this.quantitySelector.currentQty = this.quantityInput.dataset.initialValue;
          }
        }

        if (window.gtm && itemInNewCart) {
          await window.gtm.addToCart(variantId);
        }

        document.dispatchEvent(new CustomEvent('on:line-item:change', { bubbles: true }));

      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refreshCartDrawer();
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Updates cart attributes based on frozen products presence in the cart
     * @param {object} cartData - The cart data object
     * @returns {Promise<void>}
     */
    static async updateCartAttributesForFrozenProduct(cartData) {
      // Check if any products in the cart have the is_frozen flag
      const isFrozen = this.checkIfCartContainsFrozenProduct(cartData);

      // For empty cart, set _zasilkovna to true
      if (!cartData.items || cartData.items.length === 0) {
        await this.updateCartAttributes({
          _zasilkovna: 'true'
        });
        return;
      }

      // For non-empty cart, only update if _zasilkovna is not already false
      if (cartData.attributes._zasilkovna !== 'false') {
        await this.updateCartAttributes({
          _zasilkovna: isFrozen ? 'false' : 'true'
        });
      }
    }

    /**
     * Check if the cart contains any frozen products
     * @param {object} cartData - The cart data object
     * @returns {boolean} - True if frozen products are in the cart
     */
    static checkIfCartContainsFrozenProduct(cartData) {
      if (!cartData || !cartData.items || !cartData.items.length) return false;

      for (const item of cartData.items) {
        // Get product handle from the URL
        const productUrl = item.url;
        if (!productUrl) continue;

        // Extract the product handle from the URL
        const urlParts = productUrl.split('/');
        const productHandle = urlParts[urlParts.length - 1].split('?')[0];

        // Look for any element with data-frozen-product or data-product-handle attribute
        // that also has data-is-frozen="true"
        const frozenProduct = document.querySelector(`[data-is-frozen="true"]`);
        if (frozenProduct) return true;
      }

      return false;
    }

    /**
     * Updates cart attributes
     * @param {object} attributes - The attributes to update
     * @returns {Promise<object>} - The response from the fetch request
     */
    static async updateCartAttributes(attributes) {
      try {
        const response = await fetch(`${theme.routes.cart}/update.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            attributes
          })
        });

        return await response.json();
      } catch (error) {
        console.error('Error updating cart attributes:', error);
        return null;
      }
    }

    updateQuantityState(newQuantity) {
      this.quantityInput.value = String(newQuantity);
      this.quantitySelector.currentQty = newQuantity;
      this.setAttribute('data-show-quantity-selector', newQuantity > 0 ? 'true' : 'false');
    }

    async updateQuantity(quantity) {
      this.quantitySelector.setAttribute('aria-disabled', 'true');
      const lineErrors = this.querySelector('.js-product-form-errors');
      const variantId = Number(this.dataset.variantId);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      };

      const fetchRequestBody = {
        updates: {
          [variantId]: Number(quantity),
        },
        sections: "freezer-box-popup-content",
        sections_url: window.location.pathname
      }

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();
        const currentItem = oldCartData.items.find((item) => item.id === variantId);
        const oldQuantity = currentItem.quantity;
        const updatedCartItemsWithQuantity = oldCartData.items.map((item) => {
          if (item.id === variantId) {
            item.quantity = Number(quantity);
          }

          return item;
        });
        const oldFreezerBox = oldCartData.items.find((item) => item.id === currentItem.properties?._withFreezerBox);
        const sumPackageVolumes = window.sga.sumProductPackageVolumes(updatedCartItemsWithQuantity, null, oldFreezerBox?.properties?._freezerBoxAmount);

        if (currentItem.properties?._withFreezerBox) {
          if (sumPackageVolumes) {
            fetchRequestBody.updates[currentItem.properties._withFreezerBox] = Math.ceil(sumPackageVolumes);
          } else {
            fetchRequestBody.updates[currentItem.properties._withFreezerBox] = 0;
          }
        }
        if (window.gtm) {
          if (quantity > oldQuantity) {
            await window.gtm.addToCart(variantId);
          } else {
            window.gtm.removeFromCart(variantId, quantity);
          }
        }

        fetchRequestOpts.body = JSON.stringify(fetchRequestBody);

        const response = await fetch(theme.routes.cartUpdate, fetchRequestOpts);
        const data = await response.json();

        if (!response.ok) throw new Error(data.errors || response.status);

        if (sumPackageVolumes) {
          const oldFreezerBoxQuantity = oldFreezerBox?.quantity ?? 0;

          window.sga.showFreezerBoxModal(
            data,
            oldFreezerBoxQuantity,
            data.sections['freezer-box-popup-content']
          );
        }

        lineErrors.innerHTML = '';
        lineErrors.hidden = true;
      } catch (error) {
        if (/^[0-9]+$/.test(error.message)) {
          lineErrors.textContent = theme.strings.cartError;
        } else {
          lineErrors.textContent = error.message;
        }
        lineErrors.hidden = false;
        console.log(error); // eslint-disable-line

        const oldQuantity = parseInt(this.quantityInput.dataset.initialValue, 10);
        this.updateQuantityState(oldQuantity);
      } finally {
        this.updateQuantityState(parseInt(quantity, 10));
        this.quantitySelector.removeAttribute('aria-disabled');
        if (this.cartDrawer) this.cartDrawer.refreshCartDrawer();
      }
    }

    /**
     * Gets the innerHTML of an element.
     * @param {string} html - Section HTML.
     * @param {string} selector - CSS selector for the element to get the innerHTML of.
     * @returns {string}
     */
    static getElementHTML(html, selector) {
      const tmpl = document.createElement('template');
      tmpl.innerHTML = html;

      const el = tmpl.content.querySelector(selector);
      return el ? el.innerHTML : '';
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
